import { supabase } from '../lib/supabase';

const getFunctionsUrl = () => {
  const url = import.meta.env?.VITE_SUPABASE_URL;
  if (!url) return null;
  return `${url.replace(/\/$/, '')}/functions/v1`;
};

export const stripeService = {
  async createPaymentIntent(planId) {
    if (!supabase) throw new Error('Supabase not configured');
    // Refresh session so the Edge Function gets a valid token
    const { data: { session }, error: refreshError } = await supabase.auth.refreshSession();
    if (refreshError || !session?.access_token) {
      throw new Error('Please log out and log in again, then try payment.');
    }

    const baseUrl = getFunctionsUrl();
    const anonKey = import.meta.env?.VITE_SUPABASE_ANON_KEY;
    if (!baseUrl || !anonKey) throw new Error('Supabase URL or anon key missing.');

    const res = await fetch(`${baseUrl}/create-payment-intent`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session.access_token}`,
        apikey: anonKey,
      },
      body: JSON.stringify({ plan_id: planId }),
    });

    const body = await res.json().catch(() => ({}));
    if (!res.ok) {
      if (res.status === 401) {
        const detail = body?.detail ? ` (${body.detail})` : '';
        const fromGateway = !body?.error && !body?.detail;
        if (fromGateway) {
          console.warn(
            '[create-payment-intent] 401 from gateway (JWT check before function). Deploy with CLI so verify_jwt is disabled: npx supabase functions deploy create-payment-intent'
          );
        }
        throw new Error('Session expired or invalid. Please log out and log in again, then try payment.' + detail);
      }
      const msg = body?.error || res.statusText || 'Payment request failed';
      if (msg === 'Unauthorized' || String(msg).toLowerCase().includes('unauthorized')) {
        throw new Error('Session expired. Please log out and log in again, then try payment.');
      }
      if (msg === 'Stripe not configured') {
        throw new Error('Payment not configured. Add STRIPE_SECRET_KEY in Supabase → Edge Functions → Secrets.');
      }
      if (msg === 'Server configuration error') {
        throw new Error('Edge Function misconfiguration. In Supabase, set SUPABASE_URL, SUPABASE_ANON_KEY, SUPABASE_SERVICE_ROLE_KEY (usually auto-set).');
      }
      throw new Error(msg);
    }
    if (!body?.clientSecret) throw new Error('No client secret received');
    return body;
  },

  // Redirect to Stripe Checkout – works even when CSP blocks Stripe.js on your site
  async createCheckoutSession(planId, successUrl, cancelUrl) {
    if (!supabase) throw new Error('Supabase not configured');
    const { data: { session }, error: refreshError } = await supabase.auth.refreshSession();
    if (refreshError || !session?.access_token) {
      throw new Error('Please log out and log in again, then try payment.');
    }

    const baseUrl = getFunctionsUrl();
    const anonKey = import.meta.env?.VITE_SUPABASE_ANON_KEY;
    if (!baseUrl || !anonKey) throw new Error('Supabase URL or anon key missing.');

    const res = await fetch(`${baseUrl}/create-checkout-session`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session.access_token}`,
        apikey: anonKey,
      },
      body: JSON.stringify({
        plan_id: planId,
        success_url: successUrl,
        cancel_url: cancelUrl,
      }),
    });

    const body = await res.json().catch(() => ({}));
    if (!res.ok) {
      if (res.status === 401) throw new Error('Session expired. Please log out and log in again, then try payment.');
      const msg = body?.error || res.statusText || 'Payment request failed';
      if (msg === 'Stripe not configured') {
        throw new Error('Payment not configured. Add STRIPE_SECRET_KEY in Supabase → Edge Functions → Secrets.');
      }
      throw new Error(msg);
    }
    if (!body?.url) throw new Error('No checkout URL received');
    return body;
  },
};
